import type { ModuleKey } from "../modules/registry";
import type { AccessRule } from "./access";

export type PmsNavKey =
  | "live-board"
  | "reservations"
  | "room-type-availability"
  | "room-availability-calendar"
  | "guests"
  | "stay"
  | "frontdesk"
  | "housekeeping"
  | "billing"
  | "night-audit"
  | "rates"
  | "revenue"
  | "reports"
  | "interfaces"
  | "admin";

export type PmsActionKey =
  | "new-reservation"
  | "start-checkin"
  | "night-audit"
  | "walk-in"
  | "room-move"
  | "split-invoice"
  | "late-checkout"
  | "hk-task";

// Module-level access — drives sidebar visibility.
// anyPermissions is the primary check for USM users (who receive explicit permissions).
// roles is the fallback for local dev users.
export const MODULE_VISIBILITY: Partial<Record<ModuleKey, AccessRule>> = {
  identity: { roles: ["admin", "general_manager"], anyPermissions: ["users.view", "admin.access"] },
  "identity-management": { roles: ["admin", "general_manager"], anyPermissions: ["users.view", "admin.access"] },
  settings: { anyPermissions: ["settings.view", "admin.access"] },
  pms: { anyPermissions: ["pms.access"] },
  pos: { anyPermissions: ["pos.access"] },
  ems: { anyPermissions: ["ems.access"] },
  voip: { anyPermissions: ["voip.access"] },
  wms: { anyPermissions: ["wms.access"] },
  revenue: { anyPermissions: ["revenue.access"] },
  guests: { anyPermissions: ["guests.access"] },
  cctv: { anyPermissions: ["cctv.access"] },
  keycoder: { anyPermissions: ["keycoder.access"] },
  garage: { anyPermissions: ["garage.access"] },
  support: {
    roles: ["admin", "general_manager", "front_office", "housekeeping", "finance", "engineering", "supervisor"],
    anyPermissions: ["pms.access", "pos.access", "ems.access", "wms.access", "voip.access", "cctv.access", "keycoder.access", "garage.access"],
  },
  surveys: { anyPermissions: ["revenue.analytics.view", "reports.view"] },
};

// PMS sub-navigation visibility.
export const PMS_NAV_VISIBILITY: Record<PmsNavKey, AccessRule> = {
  "live-board": {
    anyPermissions: ["pms.reservations.view", "pms.rooms.view"],
    roles: ["admin", "general_manager", "front_office", "supervisor"],
  },
  reservations: { anyPermissions: ["pms.reservations.view"] },
  "room-type-availability": { anyPermissions: ["pms.rooms.view", "pms.reservations.view"] },
  "room-availability-calendar": { anyPermissions: ["pms.rooms.view"] },
  guests: { anyPermissions: ["pms.guests.view"] },
  stay: { anyPermissions: ["pms.reservations.view"] },
  frontdesk: { anyPermissions: ["pms.reservations.checkin", "pms.reservations.checkout", "pms.rooms.assign"] },
  housekeeping: { anyPermissions: ["pms.housekeeping.view"] },
  billing: { anyPermissions: ["pms.billing.view"] },
  "night-audit": { anyPermissions: ["pms.nightaudit.view", "pms.nightaudit.run"] },
  rates: { anyPermissions: ["pms.rates.view"] },
  revenue: { anyPermissions: ["revenue.analytics.view", "revenue.access", "revenue.pricing.view"] },
  reports: { anyPermissions: ["pms.reports.view", "pms.reports.financial", "reports.view"] },
  interfaces: { roles: ["admin", "engineering"], anyPermissions: ["admin.access"] },
  admin: { anyPermissions: ["admin.access"] },
};

// PMS action-level visibility (buttons, quick actions).
export const PMS_ACTION_VISIBILITY: Record<PmsActionKey, AccessRule> = {
  "new-reservation": { anyPermissions: ["pms.reservations.create"] },
  "start-checkin": { anyPermissions: ["pms.reservations.checkin"] },
  "night-audit": { anyPermissions: ["pms.nightaudit.run"] },
  "walk-in": { anyPermissions: ["pms.reservations.checkin", "pms.reservations.create"] },
  "room-move": { anyPermissions: ["pms.rooms.assign"] },
  "split-invoice": { anyPermissions: ["pms.billing.transfer"] },
  "late-checkout": { anyPermissions: ["pms.reservations.edit"] },
  "hk-task": { anyPermissions: ["pms.housekeeping.assign"] },
};
